import { Component, computed, DestroyRef, OnDestroy, OnInit, signal, TemplateRef, viewChild, ViewChild, } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable, take } from 'rxjs';
import { PreloaderColor, UIInputStyle } from '@enums/shared.enum';
import { DoorsSelectors } from '@states/doors/doors.selector-types';
import { DoorModels } from '@models/door.model';
import { DoorsActions } from '@states/doors/doors.action-types';
import { MatDialog } from '@angular/material/dialog';
import { UiSearchComponent } from '../../../../shared/ui-kit/ui-search/ui-search.component';
import { SharedSelectors } from '@states/shared/shared.selector-types';
import { features } from '@consts/text.const';
import {
  UiDoorDialogComponent,
  UiDoorDialogData
} from '../../../../shared/ui-kit/ui-door-dialog/ui-door-dialog.component';
import { UiDoorSelectorComponent } from '../../../../shared/ui-kit/ui-door-selector/ui-door-selector.component';
import { LocationSelectors } from '@states/location/location.selector-types';
import { CameraSelectors } from '@states/camera/camera.selector-types';
import { SharedActions } from '@states/shared/shared.action-types';
import { ActivatedRoute } from '@angular/router';
import * as MenuActions from '@states/menu/menu.actions';
import { MenuKey } from '@enums/menu.enum';
import { LetDirective } from '@ngrx/component';
import { TranslatePipe } from '@ngx-translate/core';
import { UiKitModule } from '../../../../shared/ui-kit/ui-kit.module';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { AuthSrcDirective } from '../../../../shared/directives/auth-src.directive';
import { UiNoDataComponent } from '@ui';
import { UiDoorIconComponent } from '../../../../shared/ui-kit/ui-door-icon/ui-door-icon.component';
import { UiPreloaderComponent } from '../../../../shared/ui-kit/ui-preloader/ui-preloader.component';
import { UiDatasetTableComponent } from '@ui/lib/ui-kit/ui-dataset-table';
import { ViewportService } from '@ui/lib/services/viewport.service';
import { createDoorsTableConfig, DoorCellContext } from './doors-table.util';
import { DoorEditModalComponent } from './components/door-edit-modal/door-edit-modal.component';
import { TruncateDirective } from '../../../../shared/directives/truncate.directive';
import { ThumbnailsService } from '../../../../development/thumbnails.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-doors',
  templateUrl: './doors.component.html',
  styleUrls: ['./doors.component.scss'],
  imports: [
    LetDirective,
    TranslatePipe,
    UiKitModule,
    MatIcon,
    FormsModule,
    AuthSrcDirective,
    UiNoDataComponent,
    UiDoorIconComponent,
    UiPreloaderComponent,
    UiDatasetTableComponent,
    TruncateDirective,
  ],
})
export class DoorsComponent implements OnInit, OnDestroy {
  public UIInputStyle = UIInputStyle;

  public noResults = features.doors.noResults;
  public noSaved = features.doors.noSaved;

  @ViewChild('searchInput') searchInput: UiSearchComponent;

  protected readonly imageCellTemplate = viewChild<TemplateRef<DoorCellContext>>('imageCell');
  protected readonly nameCellTemplate = viewChild<TemplateRef<DoorCellContext>>('nameCell');

  public editingDoorId = signal<string | null>(null);
  public editableDoor = signal<DoorModels.Door | null>(null);

  private readonly locationNames = this.store$.selectSignal(LocationSelectors.selectLocationNames);
  private readonly cameraNames = this.store$.selectSignal(CameraSelectors.selectCameraNames);

  public PreloaderColor = PreloaderColor;
  public selectAllDoors$: Observable<DoorModels.Door[]> = this.store$.pipe(
    select(DoorsSelectors.selectAllDoors),
  );
  public selectSavedDoors$: Observable<DoorModels.Door[]> = this.store$.pipe(
    select(DoorsSelectors.selectSavedDoors),
  );
  public selectUnsavedDoors$: Observable<DoorModels.Door[]> = this.store$.pipe(
    select(DoorsSelectors.selectUnsavedDoors),
  );
  public isLoading$: Observable<boolean> = this.store$.pipe(select(SharedSelectors.selectIsLoading));

  public query = '';

  public saved: DoorModels.Door[] = [];

  public get hasTableData(): boolean {
    return this.saved.length > 0;
  }

  public get displayedDoors(): DoorModels.Door[] {
    return this.query ? this.filtered(this.saved) : this.saved;
  }

  public tableConfig = computed(() => {
    const imageTemplate = this.imageCellTemplate();
    const nameTemplate = this.nameCellTemplate();
    if (!imageTemplate || !nameTemplate) {
      return null;
    }
    return createDoorsTableConfig({
      imageCellTemplate: imageTemplate,
      nameCellTemplate: nameTemplate,
      getLocationName: (row) => this.getLocationName(row),
      getCameraName: (row) => this.getCameraName(row),
      editingId: this.editingDoorId,
    });
  });

  constructor(
    private store$: Store,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private viewportService: ViewportService,
    private thumbnailsService: ThumbnailsService,
    private destroyRef: DestroyRef,
  ) {}

  public ngOnInit(): void {
    this.store$.dispatch(
      MenuActions.setActiveLevel2Menu({
        activeLevel2: MenuKey.doors,
      }),
    );
    this.store$.dispatch(SharedActions.setIsLoading({ isLoading: true }));
    this.store$.dispatch(DoorsActions.getDoors());
    this.selectSavedDoors$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((saved) => {
      this.saved = saved;
    });
    this.selectUnsavedDoors$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((unsaved) => {
      this.route.queryParams.pipe(take(1)).subscribe((params) => {
        if (params['ts']) {
          const ts = params['ts'];
          const door = unsaved.find((door) => door.timestamp === +ts);
          if (door) {
            this.add(door);
          }
        }
      });
    });
  }

  public getLocationName(row: DoorModels.Door): string {
    const names = this.locationNames();
    return names?.[row.locationId] ?? '—';
  }

  public getCameraName(row: DoorModels.Door): string {
    const names = this.cameraNames();
    return names?.[row.cameraId] ?? 'N/A';
  }

  public isEditing(doorId: string): boolean {
    return this.editingDoorId() === doorId;
  }

  public onRowAction(event: { actionId: string; row: DoorModels.Door }): void {
    if (event.actionId === 'edit') {
      const isTabletOrMobile = this.viewportService.width() < 1024;
      if (isTabletOrMobile) {
        this.openEditModal(event.row);
      } else {
        this.editDoor(event.row);
      }
    } else if (event.actionId === 'cancel') {
      this.cancelEdit();
    } else if (event.actionId === 'save') {
      this.saveDoor();
    } else if (event.actionId === 'delete') {
      this.removeDoor(event.row);
    }
  }

  public openEditModal(door: DoorModels.Door): void {
    this.dialog.open(DoorEditModalComponent, {
      data: { door },
      panelClass: 'door-edit-modal',
      width: '90vw',
      maxWidth: '500px',
    });
  }

  public editDoor(door: DoorModels.Door): void {
    this.editableDoor.set(structuredClone(door));
    this.editingDoorId.set(door.id);
  }

  public saveDoor(): void {
    const door = this.editableDoor();
    if (!door) return;
    this.store$.dispatch(DoorsActions.editDoor({ door }));
    this.cancelEdit();
  }

  public cancelEdit(): void {
    this.editableDoor.set(null);
    this.editingDoorId.set(null);
  }

  public removeDoor(door: DoorModels.Door): void {
    this.store$.dispatch(DoorsActions.removeDoor({ door }));
  }

  public selectDoors() {
    this.dialog
      .open(UiDoorSelectorComponent, {
        width: '570px',
        panelClass: 'modal-no-padding',
      })
      .afterClosed()
      .subscribe();
  }

  public search(query: string) {
    this.query = query;
  }

  public add(door: DoorModels.Door) {
    const data: UiDoorDialogData = {
      door,
    };
    this.dialog
      .open(UiDoorDialogComponent, {
        width: '400px',
        panelClass: 'modal-no-padding',
        data,
      })
      .afterClosed()
      .subscribe((val) => {
        if (val) {
          if (!!val?.delete) {
            this.store$.dispatch(DoorsActions.removeDoor({ door }));
          } else {
            this.store$.dispatch(DoorsActions.addDoor({ door: { ...door, name: val } }));
          }
        }
        this.clearQuery();
      });
  }

  // public addExisting(person: DoorModels.Door) {
  //   const data: UiPersonDialogData = {
  //     person,
  //     toExisting: true,
  //   };
  //   this.dialog.open(UiPersonDialogComponent, {
  //       height: '664px',
  //       width: '850px',
  //       maxWidth: '85vw',
  //       panelClass: 'modal-no-padding',
  //       data,
  //     })
  //     .afterClosed()
  //     .subscribe((name) => {
  //       this.clearQuery();
  //     });
  // }

  clearQuery() {
    this.query = '';
  }

  public viewMore() {
    // this.dialog.open(DoorsUnsavedDialogComponent, {
    //     width: '590px',
    //     panelClass: 'modal-no-padding',
    //   })
    //   .afterClosed()
    //   .subscribe((name) => {
    //   });
  }

  public ngOnDestroy(): void {
    this.store$.dispatch(DoorsActions.resetToInitialState());
  }

  public filtered(doors: DoorModels.Door[]) {
    return doors.filter((p) => p?.name?.toLowerCase().includes(this.query?.toLowerCase()));
  }

  public img(door: DoorModels.Door) {
    const base = this.thumbnailsService.getTrainingThumbnailsBaseUrl(door.edgeId, false);
    return `${base}/training/${door.edgeId}/${door.cameraId}/${door.closeState}`;
  }

  // public uploadImages() {
  //   this.dialog.open(DoorsUploadDialogComponent, {
  //       width: '400px',
  //       panelClass: 'modal-no-padding',
  //     })
  //     .afterClosed()
  //     .subscribe((name) => {
  //       this.store$.dispatch(DoorsActions.getDoors({ status: DoorModels.DoorStatus.Unsaved, page: 0, size: 10 }));
  //       this.store$.dispatch(DoorsActions.getDoors({ status: DoorModels.DoorStatus.Saved }));
  //     });
  // }
}
